import { gql, useMutation, useQuery } from "@apollo/client";
import currency from "currency.js";
import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useMount, useTitle } from "react-use";
import annuityImage from "../../../assets/images/annuity.svg";
import Button from "../../../components/Button";
import BirthdateInput from "../../../components/forms/BirthdateInput";
import CurrencyInput from "../../../components/forms/CurrencyInput";
import { FormField } from "../../../components/forms/shared";
import TextInput from "../../../components/forms/TextInput";
import Select from "../../../components/Select";
import {
  ROUTE_ONBOARDING_FINANCIAL,
  ROUTE_ONBOARDING_SUMMARY,
} from "../../../routes";
import { Plan, PLAN_INTEREST } from "../../../store/models/accounts";
import { calcAge, calcAnnuityBreakdown } from "../../../utils/calculations";
import { StepBack, StepContainer, StepHeader } from "../components/shared";

const SEX = [
  {
    label: "Male",
    value: "male",
  },
  {
    label: "Female",
    value: "female",
  },
  {
    label: "Intersex",
    value: "intersex",
  },
];

const Annuity = () => {
  useTitle("Onboarding - Annuity | Due");
  const history = useHistory();
  const [oneTimeContributionValue, setOneTimeContributionValue] =
    useState<string>();
  const [monthlyContributionValue, setMonthlyContributionValue] =
    useState<string>();

  // Used for Calculations
  const [birthdateValue, setBirthdateValue] = useState<string>();
  const [sexValue, setSexValue] =
    useState<{
      label: string;
      value: string;
    } | null>();
  const [retirementAgeValue, setRetirementAge] = useState<number>();
  const [oneTimeContributionCalcValue, setOneTimeContributionCalcValue] =
    useState<number>();
  const [monthlyContributionCalcValue, setMonthlyContributionCalcValue] =
    useState<number>();
  const [monthlyPayout, setMonthlyPayout] = useState<number>();

  const { data, loading, refetch } = useQuery(gql`
    {
      onboarding {
        monthlyContribution
        oneTimeContribution
        retirementAge
        plan
      }
      self {
        birthdate
      }
      profile {
        sex
      }
    }
  `);

  const [updateOnboarding] = useMutation(gql`
    mutation UpdateOnboarding($input: OnboardingMutationInput!) {
      onboarding(input: $input) {
        employment
      }
    }
  `);

  const [updateProfile] = useMutation(gql`
    mutation UpdateProfile($input: ProfileMutationInput!) {
      profile(input: $input) {
        addressLine1
      }
    }
  `);

  const [updateSelf] = useMutation(gql`
    mutation UpdateSelf($input: SelfMutationInput!) {
      self(input: $input) {
        firstName
      }
    }
  `);

  const { birthdate } =
    data && data.self
      ? data.self
      : {
          birthdate: "",
        };

  const { sex } =
    data && data.profile
      ? data.profile
      : {
          sex: "",
        };

  const { retirementAge, oneTimeContribution, monthlyContribution, plan } =
    data && data.onboarding
      ? data.onboarding
      : {
          plan: "",
          retirementAge: "",
          oneTimeContribution: "",
          monthlyContribution: "",
        };

  useMount(() => {
    document.getElementById("onboarding-container")?.scrollTo({ top: 0 });
    refetch();
  });

  useEffect(() => {
    if (!loading && data && monthlyContribution) {
      setMonthlyContributionValue(monthlyContribution);
      setMonthlyContributionCalcValue(monthlyContribution);
    }

    if (!loading && data && oneTimeContribution) {
      setOneTimeContributionValue(oneTimeContribution);
      setOneTimeContributionCalcValue(oneTimeContribution);
    }

    if (!loading && data && retirementAge) {
      setRetirementAge(parseInt(retirementAge));
    }
  }, [
    data,
    loading,
    birthdate,
    monthlyContribution,
    oneTimeContribution,
    retirementAge,
  ]);

  useEffect(() => {
    if (!loading && data && sex) {
      setSexValue(SEX.find((s) => s.value === sex) || null);
    }
  }, [data, loading, sex]);

  useEffect(() => {
    if (
      plan &&
      birthdateValue &&
      retirementAgeValue &&
      oneTimeContributionCalcValue &&
      monthlyContributionCalcValue
    ) {
      const interestRate = PLAN_INTEREST[plan as Plan];
      const age = calcAge(birthdateValue);

      if (age < retirementAgeValue) {
        const { monthlyPayout } = calcAnnuityBreakdown(
          interestRate,
          age,
          retirementAgeValue,
          0,
          oneTimeContributionCalcValue,
          monthlyContributionCalcValue
        );
        setMonthlyPayout(monthlyPayout);
      } else {
        setMonthlyPayout(undefined);
      }
    } else {
      setMonthlyPayout(undefined);
    }
  }, [
    birthdateValue,
    monthlyContributionCalcValue,
    oneTimeContributionCalcValue,
    plan,
    retirementAgeValue,
  ]);

  return (
    <StepContainer className="lg:w-8/12 min-h-0 md:w-10/12">
      <div className="w-6/12 pr-8">
        <div className="flex items-center">
          <StepBack
            className="mr-2"
            onClick={() => history.push(ROUTE_ONBOARDING_FINANCIAL)}
          />
          <StepHeader>Annuity</StepHeader>
        </div>
        <div className="prose mt-6">
          <p>
            Let set collect some numbers. This will help us understand you goals
            for your retirement. Enter all required fields to see estimation
            life time annuity payment.
          </p>
        </div>
        {monthlyPayout ? (
          <div className="my-6 bg-white px-8 py-6 rounded-md border border-gray-200">
            <div className="text-gray-400 text-sm">
              Monthly payouts at age {retirementAgeValue}
            </div>
            <div>
              <span className="text-4xl">
                {currency(monthlyPayout).format()}
              </span>
              <span className="text-gray-500">/month</span>
            </div>
          </div>
        ) : (
          <div></div>
        )}
        <img className="mt-10" src={annuityImage} alt="Profile" />
      </div>
      <div className="w-6/12">
        <div className="border border-gray-400 rounded-md bg-white p-8 mb-16">
          <Formik
            validateOnChange={false}
            validateOnBlur={true}
            initialValues={{
              birthdate,
              monthlyContribution,
              oneTimeContribution,
              retirementAge: retirementAge === 0 ? 65 : retirementAge,
              sex: "",
            }}
            validate={({ birthdate, retirementAge }) => {
              const errors: { [field: string]: string } = {};
              if (birthdate && retirementAge) {
                const age = calcAge(birthdate);
                if (age >= retirementAge) {
                  errors.retirementAge = `Retirement age must be greater than ${age}.`;
                }
              }
              return errors;
            }}
            enableReinitialize
            onSubmit={({
              birthdate,
              monthlyContribution,
              oneTimeContribution,
              retirementAge,
              sex,
            }) => {
              Promise.all([
                updateSelf({
                  variables: {
                    input: { birthdate },
                  },
                }),
                updateOnboarding({
                  variables: {
                    input: {
                      monthlyContribution,
                      oneTimeContribution,
                      retirementAge,
                    },
                  },
                }),
                updateProfile({
                  variables: {
                    input: {
                      sex,
                    },
                  },
                }),
              ]).then(() => {
                history.push(ROUTE_ONBOARDING_SUMMARY);
                refetch();
              });
            }}
          >
            {({ values, errors, setFieldValue, setErrors }) => (
              <Form>
                <div className="mb-1 text-sm">
                  How much would you like to deposit initially?
                </div>
                <FormField
                  id="oneTimeContribution"
                  error={errors.oneTimeContribution}
                >
                  <CurrencyInput
                    id="oneTimeContribution"
                    name="oneTimeContribution"
                    value={oneTimeContributionValue}
                    prefix="$"
                    placeholder="$10,000"
                    allowNegativeValue={false}
                    onValueChange={(value) => {
                      const parsedNumber = Number(value);
                      if (!isNaN(parsedNumber)) {
                        setFieldValue("oneTimeContribution", parsedNumber);
                        setOneTimeContributionValue(value);
                        setOneTimeContributionCalcValue(parsedNumber);
                      } else {
                        setFieldValue("oneTimeContribution", 0);
                        setOneTimeContributionValue("");
                        setOneTimeContributionCalcValue(undefined);
                      }
                    }}
                  />
                </FormField>
                <div className="mb-1 text-sm">
                  How much do you want to contribute every month?
                </div>
                <FormField
                  id="monthlyContribution"
                  error={errors.monthlyContribution}
                >
                  <CurrencyInput
                    id="monthlyContribution"
                    name="monthlyContribution"
                    value={monthlyContributionValue}
                    prefix="$"
                    allowNegativeValue={false}
                    placeholder="$500"
                    onValueChange={(value) => {
                      const parsedNumber = Number(value);
                      if (!isNaN(parsedNumber)) {
                        setFieldValue("monthlyContribution", parsedNumber);
                        setMonthlyContributionValue(value);
                        setMonthlyContributionCalcValue(parsedNumber);
                      } else {
                        setFieldValue("monthlyContribution", 0);
                        setMonthlyContributionValue("");
                        setMonthlyContributionCalcValue(undefined);
                      }
                    }}
                  />
                </FormField>
                <FormField
                  id="birthdate"
                  label="Birthdate"
                  error={errors.birthdate}
                >
                  <BirthdateInput
                    value={values.birthdate}
                    onChange={(value) => {
                      if (value) {
                        setFieldValue("birthdate", value);
                        setBirthdateValue(value);
                      } else {
                        setFieldValue("birthdate", "");
                      }
                      setErrors({});
                    }}
                  />
                </FormField>
                <FormField
                  id="retirementAge"
                  label="Retirement Age"
                  error={errors.retirementAge}
                >
                  <TextInput
                    type="number"
                    min={18}
                    max={120}
                    id="retirementAge"
                    name="retirementAge"
                    value={values.retirementAge}
                    onChange={(e) => {
                      setFieldValue("retirementAge", e.target.value);
                    }}
                    onBlur={(e) => {
                      setRetirementAge(e.target.valueAsNumber);
                      setErrors({});
                    }}
                  />
                </FormField>

                <FormField
                  className="w-6/12 ml-1"
                  id="sex"
                  label="Sex"
                  error={errors.sex}
                >
                  <Select
                    placeholder="Select sex..."
                    value={sexValue}
                    options={SEX}
                    onChange={(value) => {
                      if (value) {
                        setFieldValue("sex", value.value);
                        setSexValue(value);
                      }
                    }}
                  />
                </FormField>
                <Button type="submit" size="lg">
                  Continue
                </Button>
              </Form>
            )}
          </Formik>
        </div>
        <div className="w-full h-10"></div>
      </div>
    </StepContainer>
  );
};

export default Annuity;
